import { useEffect } from 'react';
import type { Path, PathValue, UseFormGetValues, UseFormSetValue } from 'react-hook-form';

interface AutofillFieldConfig<T> {
  fieldName: Path<T>;
  selector: string;
  transform?: (value: string) => string | undefined;
}

export const useAutofillDetection = <T extends object>(
  getValues: UseFormGetValues<T>,
  setValue: UseFormSetValue<T>,
  fields: AutofillFieldConfig<T>[],
  pollingInterval: number = 500
) => {
  useEffect(() => {
    const inputs = fields
      .map(({ selector, fieldName, transform }) => ({
        element: document.querySelector(selector) as HTMLInputElement | null,
        fieldName,
        transform,
      }))
      .filter(({ element }) => element !== null);

    if (inputs.length === 0) return () => {};

    // INFO: Maybe one day make this MutationObserver?
    // https://grok.com/share/bGVnYWN5_2b021d00-e8b1-41f7-8775-254ddc9feca0
    const checkAutofill = () => {
      inputs.forEach(({ element, fieldName, transform }) => {
        if (!element) return;

        const currentValue = transform ? transform(element.value) : element.value;
        const formValue = getValues(fieldName);

        if (currentValue && currentValue !== formValue) {
          setValue(fieldName, currentValue as PathValue<T, Path<T>>, { shouldValidate: true });
        }
      });
    };

    const interval = setInterval(checkAutofill, pollingInterval);
    return () => clearInterval(interval);
  }, [getValues, setValue, fields, pollingInterval]);
};

export const autofillPhoneTransform = (value: string) => {
  const transformedValue = value
    .replace(/\D/g, '')
    .replace(/^1/, '')
    .replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  if (transformedValue.length === 12) {
    return transformedValue;
  }
};

export const autofillCardTransform = (value: string) => {
  if (value.startsWith('34') || value.startsWith('37')) {
    const amexCard = value.replace(/\D/g, '').replace(/(\d{4})(\d{6})(\d{5})/, '$1-$2-$3');
    if (amexCard.length === 17) {
      return amexCard;
    }
  }
  const nonAmexAcrd = value
    .replace(/\D/g, '')
    .replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '$1-$2-$3-$4');

  if (nonAmexAcrd.length === 19) {
    return nonAmexAcrd;
  }
};

// eslint-disable-next-line complexity
export const autofillExpirationTransform = (value: string) => {
  const splitValue = value.split('/');
  if (splitValue.length === 2) {
    if (splitValue[0].length === 2) {
      if (splitValue[1].length === 2) {
        return `${splitValue[0]}/${splitValue[1]}`;
      }
      if (splitValue[1].length === 4) {
        return `${splitValue[0]}/${splitValue[1][2]}${splitValue[1][3]}`;
      }
    }
  }

  const strippedValue = value.replace(/\D/g, '');
  if (strippedValue.length === 4) {
    return strippedValue.replace(/(\d{2})(\d{2})/, '$1/$2');
  }

  if (strippedValue.length > 4) {
    return '';
  }
};
